// AiConsoleDrawer — collapsible side panel on the AI 導覽 page.
//
// Desktop layout:
//   ┌──────────────┬─────────────────┐
//   │ chat (flex-1)│  drawer (right) │  ← width persists in localStorage
//   │              │   ↕ drag handle │     drag-resize 240..720
//   └──────────────┴─────────────────┘
//
// Mobile layout (max-md):
//   ┌─────────────────────────────────┐
//   │  chat (full height)             │
//   │  ┌───────────────────────────┐  │
//   │  │ drawer bottom sheet       │  │  ← swipe handle on top
//   │  │   collapsed:    32px      │  │     snap: collapsed / half / full
//   │  │   half:        55vh       │  │
//   │  │   full:        95vh       │  │
//   │  └───────────────────────────┘  │
//   └─────────────────────────────────┘
//
// View modes: spec docs/specs/ai-mode-operations-console.md §4.5 drawer.
//   topology → renders the structural graph (AM-5a)
//   plan     → renders the propose_plan preview / apply / results
//              (AM-5b — placeholder for now)
//
// Width / state contract:
//   - URL ?drawer=topology|plan|closed (no separate `closed=1`)
//   - localStorage `location.dashboard.drawer_width` (px, integer)
//   - When `?drawer` is absent, default to "closed" so the chat reads
//     full-width on first visit.

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ChevronLeft, X } from "lucide-react";
import type { DrawerView } from "./AiConsoleIconRow.js";

const DEFAULT_WIDTH = 420;
const MIN_WIDTH = 240;
const MAX_WIDTH = 720;
const STORAGE_KEY = "location.dashboard.drawer_width";

const MOBILE_SNAP_COLLAPSED_PX = 32;
const MOBILE_SNAP_HALF_VH = 55;
const MOBILE_SNAP_FULL_VH = 95;

export interface AiConsoleDrawerProps {
  view: DrawerView;
  onChangeView: (next: DrawerView) => void;
  /** Drawer body — caller renders the active mode. AM-5a typically
   *  passes a TopologyCanvas wrapper; AM-5b will pass a PlanPanel
   *  alongside (caller picks based on `view`). */
  children: React.ReactNode;
  /** Optional header right-side controls — e.g. "看完整結構" button
   *  when topology is focused, or close-x for plan mode. */
  headerExtras?: React.ReactNode;
  /** Title shown in the drawer header. Defaults to "結構" for
   *  topology / "計畫" for plan. */
  title?: string;
}

function readWidthFromLocalStorage(): number {
  if (typeof window === "undefined") return DEFAULT_WIDTH;
  const raw = window.localStorage.getItem(STORAGE_KEY);
  if (!raw) return DEFAULT_WIDTH;
  const n = parseInt(raw, 10);
  if (!Number.isFinite(n)) return DEFAULT_WIDTH;
  return Math.max(MIN_WIDTH, Math.min(MAX_WIDTH, n));
}

function writeWidthToLocalStorage(px: number): void {
  try { window.localStorage.setItem(STORAGE_KEY, String(px)); }
  catch { /* private mode / quota — silent */ }
}

export default function AiConsoleDrawer({
  view, onChangeView, children, headerExtras, title,
}: AiConsoleDrawerProps): React.ReactElement | null {
  const [width, setWidth] = useState<number>(() => readWidthFromLocalStorage());
  // Mobile snap point — collapsed / half / full. Defaults to half
  // whenever the drawer transitions from "closed" → open on mobile.
  const [mobileSnap, setMobileSnap] = useState<"collapsed" | "half" | "full">(
    view === "closed" ? "collapsed" : "half",
  );

  useEffect(() => {
    if (view === "closed") setMobileSnap("collapsed");
    else if (mobileSnap === "collapsed") setMobileSnap("half");
  }, [view]); // eslint-disable-line react-hooks/exhaustive-deps

  // Broadcast a「drawer just closed」 event so the chat panel can scroll
  // to bottom (the chat just expanded back to full viewport and the
  // operator wants to see the latest messages — typically a freshly-
  // injected LocalCard like the binding-code QR).
  const prevViewRef = useRef<DrawerView>(view);
  useEffect(() => {
    if (prevViewRef.current !== "closed" && view === "closed") {
      window.dispatchEvent(new CustomEvent("ai-console-drawer:closed"));
    }
    prevViewRef.current = view;
  }, [view]);

  // ── Drag-resize (desktop only) ───────────────────────────────────────
  const onResizePointerDown = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    e.preventDefault();
    const startX = e.clientX;
    const startWidth = width;
    // Use document-level pointermove so the cursor can leave the
    // handle's bounds without the drag dying.
    const move = (ev: PointerEvent) => {
      const next = Math.max(MIN_WIDTH, Math.min(MAX_WIDTH, startWidth - (ev.clientX - startX)));
      setWidth(next);
    };
    const up = () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
      window.removeEventListener("pointercancel", up);
      setWidth((w) => { writeWidthToLocalStorage(w); return w; });
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
    window.addEventListener("pointercancel", up);
  }, [width]);

  // ── Mobile snap pointer handlers ─────────────────────────────────────
  // Live drag: sheet height follows finger; on release, snap based on
  // final height vs viewport thresholds.
  //   ≥ 75vh    → full (auto-expand)
  //   ≤ 20vh    → closed (auto-dismiss)
  //   in-between → half
  const [dragHeightPx, setDragHeightPx] = useState<number | null>(null);
  const onSheetHandlePointerDown = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    e.preventDefault();
    const startY = e.clientY;
    const viewportH = typeof window !== "undefined" ? window.innerHeight : 800;
    const startHeightPx =
      mobileSnap === "full" ? viewportH * (MOBILE_SNAP_FULL_VH / 100)
      : mobileSnap === "half" ? viewportH * (MOBILE_SNAP_HALF_VH / 100)
      : MOBILE_SNAP_COLLAPSED_PX;
    const move = (ev: PointerEvent) => {
      const delta = ev.clientY - startY;
      const next = Math.max(MOBILE_SNAP_COLLAPSED_PX, Math.min(viewportH, startHeightPx - delta));
      setDragHeightPx(next);
    };
    const up = (ev: PointerEvent) => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
      window.removeEventListener("pointercancel", up);
      const finalDelta = ev.clientY - startY;
      const finalHeight = Math.max(MOBILE_SNAP_COLLAPSED_PX, Math.min(viewportH, startHeightPx - finalDelta));
      const upperThresholdPx = viewportH * 0.75;
      const lowerThresholdPx = viewportH * 0.20;
      if (finalHeight >= upperThresholdPx) {
        setMobileSnap("full");
      } else if (finalHeight <= lowerThresholdPx) {
        setMobileSnap("collapsed");
        onChangeView("closed");
      } else {
        setMobileSnap("half");
      }
      setDragHeightPx(null);
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
    window.addEventListener("pointercancel", up);
  }, [mobileSnap, onChangeView]);

  const resolvedTitle = useMemo(() => {
    if (title) return title;
    if (view === "topology") return "結構";
    if (view === "plan") return "計畫預覽";
    if (view === "form") return "新增表單";
    return "";
  }, [title, view]);

  // Drawer closed on desktop = render nothing (the icon row stays in
  // PageHeader so the operator can reopen). On mobile we keep a 32px
  // handle stuck to the bottom edge.
  if (view === "closed") {
    return null;
  }

  return (
    <>
      {/* ── Desktop side panel ─────────────────────────────────────── */}
      <aside
        role="complementary"
        aria-label="AI 導覽抽屜"
        className="hidden md:flex flex-col flex-shrink-0 border-l border-border bg-bg-surface overflow-hidden relative"
        style={{ width: `${width}px` }}
      >
        {/* Drag handle on the LEFT edge (4px wide hot zone) */}
        <div
          role="separator"
          aria-orientation="vertical"
          aria-label="拖拉調整抽屜寬度"
          onPointerDown={onResizePointerDown}
          className="absolute inset-y-0 left-0 w-1 cursor-col-resize hover:bg-accent/30 active:bg-accent/60 transition-colors"
        />
        <div className="flex items-center justify-between px-3 py-2 border-b border-border flex-shrink-0">
          <span className="text-xs font-semibold text-text-primary">{resolvedTitle}</span>
          <div className="flex items-center gap-1">{headerExtras}</div>
        </div>
        <div className="flex-1 min-h-0 relative overflow-hidden">
          {children}
        </div>
      </aside>

      {/* ── Mobile bottom sheet ────────────────────────────────────── */}
      {/* Backdrop above the sheet — tap to close. Standard mobile
          bottom-sheet pattern; without it operators have to swipe
          down twice to fully dismiss. Only mounts while the sheet is
          actually expanded (not collapsed) so the 32px handle alone
          doesn't intercept clicks. */}
      {mobileSnap !== "collapsed" && (
        <div
          className="md:hidden fixed inset-x-0 top-0 z-20"
          style={{
            bottom: mobileSnap === "full"
              ? `${MOBILE_SNAP_FULL_VH}vh`
              : `${MOBILE_SNAP_HALF_VH}vh`,
          }}
          onClick={() => onChangeView("closed")}
          aria-hidden="true"
        />
      )}
      <aside
        role="complementary"
        aria-label="AI 導覽抽屜（行動）"
        className="md:hidden fixed inset-x-0 bottom-0 z-30 bg-bg-surface border-t border-border shadow-2xl rounded-t-xl flex flex-col"
        style={{
          height: dragHeightPx !== null
            ? `${dragHeightPx}px`
            : mobileSnap === "collapsed" ? `${MOBILE_SNAP_COLLAPSED_PX}px`
            : mobileSnap === "half" ? `${MOBILE_SNAP_HALF_VH}vh`
            : `${MOBILE_SNAP_FULL_VH}vh`,
          // No CSS transition while finger is dragging — height must
          // track touchmove pixel-for-pixel. Re-enabled on release so
          // the snap-to-nearest animation is smooth.
          transition: dragHeightPx !== null ? "none" : "height 200ms ease",
          touchAction: "none",
        }}
      >
        {/* Swipe handle bar */}
        <div
          role="separator"
          aria-orientation="horizontal"
          aria-label="拖拉切換抽屜高度"
          onPointerDown={onSheetHandlePointerDown}
          className="flex-shrink-0 flex items-center justify-center py-2 cursor-grab active:cursor-grabbing touch-none"
        >
          <span className="h-1 w-10 rounded-full bg-text-muted/40" />
        </div>
        {mobileSnap !== "collapsed" && (
          <>
            <div className="flex items-center justify-between px-3 py-2 border-b border-border flex-shrink-0">
              <span className="text-xs font-semibold text-text-primary">{resolvedTitle}</span>
              <div className="flex items-center gap-1">
                {headerExtras}
                {mobileSnap === "full" ? (
                  // Full mode → X close (operator dragged past the upper
                  // threshold to enter full; the way out is dismiss, not
                  // collapse back to half).
                  <button
                    type="button"
                    aria-label="關閉抽屜"
                    onClick={() => onChangeView("closed")}
                    className="p-1 rounded hover:bg-bg-hover text-text-muted hover:text-text-primary"
                  >
                    <X className="h-4 w-4" />
                  </button>
                ) : (
                  <button
                    type="button"
                    aria-label="展開全屏"
                    onClick={() => setMobileSnap("full")}
                    className="p-1 rounded hover:bg-bg-hover text-text-muted hover:text-text-primary"
                  >
                    <ChevronLeft className="h-4 w-4 -rotate-90" />
                  </button>
                )}
              </div>
            </div>
            <div className="flex-1 min-h-0 relative overflow-hidden">
              {children}
            </div>
          </>
        )}
      </aside>
    </>
  );
}
